import { useState } from 'react';
import { Link } from 'react-router-dom';
import { updateQuestion } from '../services/questions';
import { ApiError } from '@admin/services/api';
import { useToastStore } from '@admin/stores/toastStore';
import { parseValidAnswers } from './parseValidAnswers';
import { arrayToText } from './QuestionForm';
import { questionLanguageVisibility } from './contentLanguages';
import type { QuestionListItem } from '@games/word-clash/shared/types';

interface ReviewCardProps {
  question: QuestionListItem;
  disabled: boolean;
  onApprove: () => void;
  onReject: () => void;
  /**
   * Called by the card after a successful inline-edit save so the parent
   * page can swap the row in its items list. The card flips back to display
   * mode using the returned values.
   */
  onUpdated: (updated: QuestionListItem) => void;
}

interface EditDraft {
  promptEn: string;
  promptTr: string;
  validAnswersText: string;
  validAnswersTrText: string;
  specialAnswersText: string;
  specialAnswersTrText: string;
}

function draftFromQuestion(q: QuestionListItem): EditDraft {
  return {
    promptEn: q.promptEn,
    promptTr: q.promptTr,
    validAnswersText: arrayToText(q.validAnswers),
    validAnswersTrText: arrayToText(q.validAnswersTr),
    specialAnswersText: arrayToText(q.specialAnswers),
    specialAnswersTrText: arrayToText(q.specialAnswersTr),
  };
}

/**
 * Single card in the review queue. Owns its own edit-mode state so the
 * parent page only has to think about the list + per-row review actions.
 *
 * Edit mode is intentionally focused — only the prompt + answers + special
 * answers are editable inline. Less common fields (aliases, contentFlags,
 * plausible wrong answers, category, difficulty) require the full editor at
 * /questions/:id — there's a link in the action row.
 */
export default function ReviewCard({
  question,
  disabled,
  onApprove,
  onReject,
  onUpdated,
}: ReviewCardProps) {
  const showToast = useToastStore((s) => s.showToast);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState<EditDraft>(() =>
    draftFromQuestion(question),
  );
  const [saving, setSaving] = useState(false);

  function enterEdit() {
    setDraft(draftFromQuestion(question));
    setMode('edit');
  }

  function cancelEdit() {
    setDraft(draftFromQuestion(question));
    setMode('view');
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await updateQuestion(question.id, {
        promptEn: draft.promptEn.trim(),
        promptTr: draft.promptTr.trim(),
        validAnswers: parseValidAnswers(draft.validAnswersText),
        validAnswersTr: parseValidAnswers(draft.validAnswersTrText),
        specialAnswers: parseValidAnswers(draft.specialAnswersText),
        specialAnswersTr: parseValidAnswers(draft.specialAnswersTrText),
      });
      onUpdated(updated);
      setMode('view');
      showToast('Question updated', 'success');
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Update failed';
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const q = question;
  const idBase = `review-${q.id}`;
  // Only the language(s) this question is actually served in. An en-only row
  // showing an empty Turkish column is indistinguishable from one whose
  // Turkish content is merely unwritten — see `contentLanguages.ts`.
  const { showEnglish, showTurkish } = questionLanguageVisibility(q.contentFlags);

  return (
    <li
      className="rounded border border-zinc-200 bg-white p-4 shadow-sm"
      data-testid={`review-card-${q.id}`}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
        <span className="rounded bg-zinc-100 px-2 py-0.5 font-medium">
          {q.type}
        </span>
        <span className="rounded bg-zinc-100 px-2 py-0.5">{q.difficulty}</span>
        <span className="rounded bg-zinc-100 px-2 py-0.5">{q.category}</span>
        {q.contentFlags.length > 0 ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-800">
            {q.contentFlags.join(', ')}
          </span>
        ) : null}
        {!q.isActive ? (
          <span className="rounded bg-red-100 px-2 py-0.5 text-red-800">
            inactive
          </span>
        ) : null}
      </div>

      {mode === 'view' ? (
        <dl className="space-y-2 text-sm">
          {showEnglish && (
            <ViewRow
              label="Prompt (EN)"
              testId={`review-prompt-en-${q.id}`}
              value={q.promptEn}
            />
          )}
          {showTurkish && (
            <ViewRow
              label="Prompt (TR)"
              testId={`review-prompt-tr-${q.id}`}
              value={q.promptTr}
            />
          )}
          {showEnglish && (
            <ViewRow
              label={`Valid answers (EN, ${q.validAnswers.length})`}
              testId={`review-answers-en-${q.id}`}
              value={q.validAnswers.join(', ')}
            />
          )}
          {showTurkish && (
            <ViewRow
              label={`Valid answers (TR, ${q.validAnswersTr.length})`}
              testId={`review-answers-tr-${q.id}`}
              value={q.validAnswersTr.join(', ')}
            />
          )}
          {showEnglish && (
            <ViewRow
              label={`Special answers (EN, ${q.specialAnswers.length})`}
              testId={`review-special-en-${q.id}`}
              value={q.specialAnswers.join(', ')}
            />
          )}
          {showTurkish && (
            <ViewRow
              label={`Special answers (TR, ${q.specialAnswersTr.length})`}
              testId={`review-special-tr-${q.id}`}
              value={q.specialAnswersTr.join(', ')}
            />
          )}
        </dl>
      ) : (
        <div className="space-y-3 text-sm">
          {showEnglish && (
            <FieldRow label="Prompt (EN)" testId={`${idBase}-input-promptEn`}>
              <input
                type="text"
                value={draft.promptEn}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, promptEn: e.target.value }))
                }
                data-testid={`${idBase}-input-promptEn`}
                className="w-full rounded border border-zinc-300 px-2 py-1"
              />
            </FieldRow>
          )}
          {showTurkish && (
            <FieldRow label="Prompt (TR)" testId={`${idBase}-input-promptTr`}>
              <input
                type="text"
                value={draft.promptTr}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, promptTr: e.target.value }))
                }
                data-testid={`${idBase}-input-promptTr`}
                className="w-full rounded border border-zinc-300 px-2 py-1"
              />
            </FieldRow>
          )}
          {showEnglish && (
            <FieldRow
              label="Valid answers (EN, one per line or comma-separated)"
              testId={`${idBase}-input-validAnswers`}
            >
              <textarea
                rows={4}
                value={draft.validAnswersText}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, validAnswersText: e.target.value }))
                }
                data-testid={`${idBase}-input-validAnswers`}
                className="w-full rounded border border-zinc-300 px-2 py-1 font-mono"
              />
            </FieldRow>
          )}
          {showTurkish && (
            <FieldRow
              label="Valid answers (TR)"
              testId={`${idBase}-input-validAnswersTr`}
            >
              <textarea
                rows={4}
                value={draft.validAnswersTrText}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    validAnswersTrText: e.target.value,
                  }))
                }
                data-testid={`${idBase}-input-validAnswersTr`}
                className="w-full rounded border border-zinc-300 px-2 py-1 font-mono"
              />
            </FieldRow>
          )}
          {showEnglish && (
            <FieldRow
              label="Special answers (EN)"
              testId={`${idBase}-input-specialAnswers`}
            >
              <textarea
                rows={2}
                value={draft.specialAnswersText}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    specialAnswersText: e.target.value,
                  }))
                }
                data-testid={`${idBase}-input-specialAnswers`}
                className="w-full rounded border border-zinc-300 px-2 py-1 font-mono"
              />
            </FieldRow>
          )}
          {showTurkish && (
            <FieldRow
              label="Special answers (TR)"
              testId={`${idBase}-input-specialAnswersTr`}
            >
              <textarea
                rows={2}
                value={draft.specialAnswersTrText}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    specialAnswersTrText: e.target.value,
                  }))
                }
                data-testid={`${idBase}-input-specialAnswersTr`}
                className="w-full rounded border border-zinc-300 px-2 py-1 font-mono"
              />
            </FieldRow>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        {mode === 'view' ? (
          <>
            <button
              type="button"
              onClick={onApprove}
              disabled={disabled}
              data-testid={`review-approve-${q.id}`}
              className="rounded bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={onReject}
              disabled={disabled}
              data-testid={`review-reject-${q.id}`}
              className="rounded border border-red-300 bg-white px-4 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={enterEdit}
              disabled={disabled}
              data-testid={`review-edit-${q.id}`}
              className="rounded border border-zinc-300 bg-white px-4 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              Edit
            </button>
            <Link
              to={`/questions/${q.id}`}
              data-testid={`review-full-editor-${q.id}`}
              className="ml-auto text-xs text-zinc-500 underline hover:text-zinc-700"
            >
              Full editor →
            </Link>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              data-testid={`review-save-${q.id}`}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              data-testid={`review-cancel-${q.id}`}
              className="rounded border border-zinc-300 bg-white px-4 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * One `<dt>/<dd>` pair in view mode. Extracted so the six rows can be
 * rendered per content language without six near-identical copies of the
 * markup; an empty value renders the em-dash placeholder.
 */
function ViewRow({
  label,
  testId,
  value,
}: {
  label: string;
  testId: string;
  value: string;
}) {
  return (
    <div>
      <dt className="font-medium text-zinc-700">{label}</dt>
      <dd className="mt-0.5 text-zinc-700" data-testid={testId}>
        {value.length > 0 ? value : <span className="text-zinc-400">—</span>}
      </dd>
    </div>
  );
}

function FieldRow({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block" data-testid={`${testId}-row`}>
      <span className="mb-1 block font-medium text-zinc-700">{label}</span>
      {children}
    </label>
  );
}
